import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ArrowRight, Calendar, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { getProject, getExecutionSteps } from '../api';
import { toast } from '../hooks/use-toast';

const ProjectExecutionSteps = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [executionSteps, setExecutionSteps] = useState([]);
  const [executionRecords, setExecutionRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newRecord, setNewRecord] = useState({
    locationOrStage: '',
    date: (() => {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    })(),
    step: '',
    endDate: '',
    notes: ''
  });

  useEffect(() => {
    loadProjectData();
    loadExecutionSteps();
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      const data = await getProject(projectId);
      setProject(data);
      
      // Load execution records from localStorage for this project
      const saved = localStorage.getItem(`project_execution_${projectId}`);
      if (saved) {
        setExecutionRecords(JSON.parse(saved));
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את הפרויקט',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadExecutionSteps = async () => {
    try {
      const data = await getExecutionSteps();
      setExecutionSteps(data);
    } catch (error) {
      console.error('Error loading execution steps:', error);
    }
  };

  const handleAddRecord = () => {
    if (!newRecord.date || !newRecord.step) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא תאריך ושלב ביצוע',
        variant: 'destructive'
      });
      return;
    }

    const record = {
      id: Date.now().toString(),
      locationOrStage: newRecord.locationOrStage,
      date: newRecord.date,
      step: newRecord.step,
      endDate: newRecord.endDate,
      notes: newRecord.notes,
      createdAt: new Date().toISOString()
    };

    const updated = [...executionRecords, record];
    setExecutionRecords(updated);
    
    // Save to localStorage
    localStorage.setItem(`project_execution_${projectId}`, JSON.stringify(updated));
    
    // Reset form
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setNewRecord({
      locationOrStage: '',
      date: todayString,
      step: '',
      endDate: '',
      notes: ''
    });

    toast({
      title: 'הצלחה',
      description: 'רשומת ביצוע נוספה בהצלחה'
    });
  };

  const handleDeleteRecord = (recordId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק רשומה זו?')) {
      const updated = executionRecords.filter(r => r.id !== recordId);
      setExecutionRecords(updated);
      localStorage.setItem(`project_execution_${projectId}`, JSON.stringify(updated));
      
      toast({
        title: 'הצלחה',
        description: 'הרשומה נמחקה בהצלחה'
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-red-600">פרויקט לא נמצא</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">שלבי ביצוע פרויקט</h1>
            <p className="text-xl text-gray-600">{project.projectName}</p>
            <p className="text-lg text-gray-500">לקוח: {project.customerName}</p>
          </div>
          <Button
            onClick={() => navigate(`/project-form?id=${projectId}`)}
            variant="outline"
            className="border-2 hover:bg-gray-50 transition-colors"
          >
            <ArrowRight className="ml-2 w-5 h-5" />
            חזור לפרויקט
          </Button>
        </div>

        {/* Add New Record */}
        <Card className="shadow-xl mb-6">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Plus className="w-6 h-6" />
              הוסף רשומת ביצוע חדשה
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid gap-4">
              {/* Row 0: Location or Stage */}
              <div className="space-y-2">
                <Label htmlFor="locationOrStage" className="text-base font-semibold text-gray-700">
                  מיקום או שלב עבודה
                </Label>
                <textarea
                  id="locationOrStage"
                  value={newRecord.locationOrStage}
                  onChange={(e) => setNewRecord({ ...newRecord, locationOrStage: e.target.value })}
                  placeholder="הזן מיקום או תיאור שלב העבודה..."
                  rows="2"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none resize-none"
                />
              </div>

              {/* Row 1: Date, Step, End Date */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-base font-semibold">
                    תאריך התחלה
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      id="date"
                      value={newRecord.date}
                      onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                      className="w-full pr-10 p-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="step" className="text-base font-semibold">
                    שלב ביצוע
                  </Label>
                  <Select value={newRecord.step} onValueChange={(value) => setNewRecord({ ...newRecord, step: value })}>
                    <SelectTrigger className="w-full text-base border-2 border-gray-200 focus:border-green-500">
                      <SelectValue placeholder="בחר שלב..." />
                    </SelectTrigger>
                    <SelectContent>
                      {executionSteps.map((step) => (
                        <SelectItem key={step.id} value={step.name} className="text-base">
                          {step.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-base font-semibold">
                    תאריך סיום
                  </Label>
                  <div className="relative">
                    <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      id="endDate"
                      value={newRecord.endDate}
                      onChange={(e) => setNewRecord({ ...newRecord, endDate: e.target.value })}
                      className="w-full pr-10 p-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Notes and Button */}
              <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-base font-semibold">
                    הערות
                  </Label>
                  <textarea
                    id="notes"
                    value={newRecord.notes}
                    onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
                    placeholder="הזן הערות..."
                    rows="2"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none resize-none"
                  />
                </div>

                <Button
                  onClick={handleAddRecord}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 px-8 whitespace-nowrap"
                >
                  <Plus className="ml-2 w-5 h-5" />
                  הוסף רשומה
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Execution Records Table */}
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <CardTitle className="text-xl font-bold">רשומות ביצוע</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {executionRecords.length === 0 ? (
              <div className="text-center py-16">
                <CheckCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl text-gray-500">אין רשומות ביצוע עדיין</p>
                <p className="text-gray-400 mt-2">הוסף רשומה ראשונה למעלה</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-purple-100">
                      <th className="p-4 text-right font-bold text-gray-800 border-b-2 border-purple-200">#</th>
                      <th className="p-4 text-right font-bold text-gray-800 border-b-2 border-purple-200">מיקום/שלב עבודה</th>
                      <th className="p-4 text-right font-bold text-gray-800 border-b-2 border-purple-200">תאריך התחלה</th>
                      <th className="p-4 text-right font-bold text-gray-800 border-b-2 border-purple-200">תאריך סיום</th>
                      <th className="p-4 text-right font-bold text-gray-800 border-b-2 border-purple-200">שלב ביצוע</th>
                      <th className="p-4 text-right font-bold text-gray-800 border-b-2 border-purple-200">הערות</th>
                      <th className="p-4 text-center font-bold text-gray-800 border-b-2 border-purple-200">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executionRecords.map((record, index) => (
                      <tr key={record.id} className={index % 2 === 0 ? 'bg-white hover:bg-purple-50' : 'bg-gray-50 hover:bg-purple-50'}>
                        <td className="p-4 border-b border-gray-200">{index + 1}</td>
                        <td className="p-4 border-b border-gray-200">
                          {record.locationOrStage || '-'}
                        </td>
                        <td className="p-4 border-b border-gray-200 font-semibold">
                          {formatDate(record.date)}
                        </td>
                        <td className="p-4 border-b border-gray-200 font-semibold">
                          {record.endDate ? formatDate(record.endDate) : '-'}
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">
                            <CheckCircle className="w-4 h-4" />
                            {record.step}
                          </span>
                        </td>
                        <td className="p-4 border-b border-gray-200">
                          {record.notes || '-'}
                        </td>
                        <td className="p-4 border-b border-gray-200 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRecord(record.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectExecutionSteps;
